import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, forwardRef, HostBinding, Input, Optional, Self } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Project, ProjectFilters } from 'src/app/commons/models/project.model';
import * as ProjectActions from 'src/app/store/actions/project.actions';
import { AppState } from 'src/app/store/reducers';
import * as ProjectSelectors from 'src/app/store/selectors/project.selectors';


export const PROJECT_FIELD_MAT_FORM_FIELD_CONTROL: any = {
  provide: MatFormFieldControl,
  useExisting: forwardRef(() => ProjectFieldComponent),
};

@Component({
  selector: "app-project-field",
  templateUrl: "./project-field.component.html",
  styleUrls: ["./project-field.component.scss"],
  providers: [PROJECT_FIELD_MAT_FORM_FIELD_CONTROL],
  host: {
    "[class.floating]": "shouldLabelFloat",
    "[id]": "id",
  },
})
export class ProjectFieldComponent
  implements ControlValueAccessor, MatFormFieldControl<Project> {
  private unsubscribe$ = new Subject<void>();
  private _value: Project;
  private _disabled = false;

  private _required = false;

  get errorState(): boolean {
    return !this.ngControl.valid;
  }
  focused: boolean; //TODO
  controlType?: string = "project-selection-field";
  autofilled?: boolean;
  projectAriaDescribedBy?: string;
  currentFormControlName: string;


  @Input()
  projectFilters: ProjectFilters = {
    trashed: false
  };

  @Input()
  showPromoFilter: boolean = true;
  @Input()
  showClientFilter: boolean = true;
  @Input()
  canSelectClosed: boolean = true;

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    @Optional() @Self() public ngControl: NgControl
  ) {
    this.actions$
      .pipe(
        ofType(ProjectActions.projectSelected),
        takeUntil(this.unsubscribe$),
        withLatestFrom(this.store$.select(ProjectSelectors.getCurrentFormControlName)),
        filter(([_, currentFormControlName]) => currentFormControlName == this.currentFormControlName),
        map(([{ project }, _]) => (project ? new Project(project) : null))
      )
      .subscribe((project) => (this.value = project));
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  stateChanges = new Subject<void>();
  id: string;

  ngAfterContentInit(): void {
    const control = this.ngControl && this.ngControl.control;
    if (control) {
      this.currentFormControlName = `${this.getControlName(control)}_${Math.random()}`;
    }
  }

  private getControlName(c: AbstractControl): string | null {
    const formGroup = c.parent.controls;
    return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  get empty() {
    return !this.value;
  }

  @HostBinding("class.floating")
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get required() {
    if (this.disabled) return false;
    return this._required || this.ngControl?.control?.hasValidator(Validators.required) || false;
  }

  @Input()
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    //TODO
  }
  onContainerClick(event: MouseEvent): void {
    if (!this.disabled) {
      if ((event.target as Element).tagName.toLowerCase() != "mat-icon") {
        this.selectProject();
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.stateChanges.complete();
  }

  selectProject() {
    this.store$.dispatch(ProjectActions.selectProject({ filters: this.projectFilters, currentFormControlName: this.currentFormControlName, showPromoFilter: this.showPromoFilter, showClientFilter: this.showClientFilter, canSelectClosed: this.canSelectClosed }));
  }

  clearProject() {
    this.value = null;
  }

  set value(value: Project) {
    this._value = value;
    this.notifyValueChange();
    this.stateChanges.next();
  }

  get value(): Project {
    return this._value;
  }

  onChange: (value) => {};
  onTouched: () => {};

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  writeValue(obj: Project): void {
    this._value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
